import { getRuntimeConfig } from '@/shared/runtimeConfig';
import { isLegacyDarkModeEnabled } from './darkMode';
import { getActiveLegacyCulture } from './culture';

/**
 * Sestavení URL pro Magic iframe v Phoenix-On-Top režimech.
 *
 * Magic base URL přichází z backendového runtime configu (`/api/config/runtime`).
 * Pokud není nakonfigurované, vrací prázdný string — komponenty by měly takový stav detekovat
 * a zobrazit chybu (Phoenix-primary režim by bez Magicu nedával smysl).
 */
export const EMBED_PARAM_NAME = 'embeddedIn';
export const EMBED_VALUE_CONTROL = 'phoenix-control';
export const EMBED_VALUE_SINGLE_FORM = 'phoenix';
export const FORM_PARAM_NAME = 'form';
/** URL param, kterým Phoenix při loadu řekne Magicu, ať se rovnou vyrenderuje v dark (`darkMode=1`). */
export const DARK_MODE_PARAM_NAME = 'darkMode';
/** URL param se sdíleným resource revision (cache-busting) — přebírá ho Magic dev provider. */
export const REVISION_PARAM_NAME = 'revision';
/** URL param, kterým Phoenix řekne Magicu, v jakém jazyce (kultuře) má renderovat (cs/en) — viz [culture.ts](./culture.ts). */
export const CULTURE_PARAM_NAME = 'culture';

/**
 * Sdílený resource `revision` z hlavního Magic control iframu. Nastaví ho `MagicControlBridge`,
 * jakmile dorazí `desktop/menu-ready` (kde ho Magic přibalí). Předává se do URL single-form oken
 * legacy agend, aby všechna razítkovala scripty stejnou hodnotou → cache hit (jinak na DEVu každý
 * iframe dostane nový timestamp a stahuje vše znovu). Dokud není známý, okna se otevřou bez něj.
 */
let sharedMagicRevision: string | null = null;

/** Uloží/zruší sdílený Magic revision (volá `MagicControlBridge` z `desktop/menu-ready`). */
export function setMagicResourceRevision(revision: string | null | undefined): void {
  sharedMagicRevision = revision && revision.trim() ? revision : null;
}

/** Vrátí Magic base URL z runtime configu nebo prázdný string. */
export function getMagicBaseUrl(): string {
  return getRuntimeConfig().legacyMagic.baseUrl;
}

function joinUrl(base: string, query: Record<string, string>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    search.set(key, value);
  }
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}${search.toString()}`;
}

/**
 * URL pro skrytou Magic control instanci (jeden iframe na celou Phoenix session).
 * Magic se ohlásí přes `desktop/menu-ready` a slouží jako dispatcher menu akcí.
 */
/** URL param s jednorázovým OTT – Magic ho na serveru zredeemuje a založí si FormsAuth cookie. */
export const OTT_PARAM_NAME = 'ott';

export function buildMagicControlUrl(ott?: string): string {
  const base = getMagicBaseUrl();
  if (!base) return '';
  const query: Record<string, string> = { [EMBED_PARAM_NAME]: EMBED_VALUE_CONTROL };
  if (ott) query[OTT_PARAM_NAME] = ott;
  // Jazyk řídí Phoenix — Magic ho při OTT redeemu uloží do nastavení uživatele (viz culture.ts).
  query[CULTURE_PARAM_NAME] = getActiveLegacyCulture();
  if (isLegacyDarkModeEnabled()) query[DARK_MODE_PARAM_NAME] = '1';
  return joinUrl(base, query);
}

/**
 * URL pro single-form Magic instanci (jeden iframe na otevřenou Phoenix legacy agendu).
 * @param formGlobalName Magic Form GlobalName (např. `Organization.Edit`)
 * @param formParams Dictionary parametrů formuláře (předají se Magic Navigatoru)
 */
export function buildMagicSingleFormUrl(
  formGlobalName: string,
  formParams?: Record<string, unknown>
): string {
  const base = getMagicBaseUrl();
  if (!base) return '';

  const query: Record<string, string> = {
    [EMBED_PARAM_NAME]: EMBED_VALUE_SINGLE_FORM,
    [FORM_PARAM_NAME]: formGlobalName,
  };
  if (formParams) {
    for (const [key, value] of Object.entries(formParams)) {
      if (value === undefined || value === null) continue;
      query[key] = String(value);
    }
  }
  // Až po formParams, ať náš darkMode flag nepřebije případný stejnojmenný form param.
  if (isLegacyDarkModeEnabled()) query[DARK_MODE_PARAM_NAME] = '1';
  // Sdílený revision z hlavního Magic iframu → stejná cache pro všechna okna legacy agend.
  if (sharedMagicRevision) query[REVISION_PARAM_NAME] = sharedMagicRevision;
  return joinUrl(base, query);
}
